/**
 * CreateApiKeyModal
 *
 * Two-step modal for generating a new API Key:
 *  Step 1 — Fill in name, optional expiry and notes
 *  Step 2 — Display the generated credentials (key + secret) with copy buttons
 *
 * The API Secret is shown ONLY once. Users must acknowledge they copied it before closing.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Key, Copy, Check, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { ApiKey, ApiKeyCreateRequest } from '../../../types/apiKey';
import { createApiKey } from '../../../services/apiKeyService';

interface CreateApiKeyModalProps {
  workspaceId: number;
  onClose: () => void;
  onSuccess: (newKey: ApiKey) => void;
}

type Step = 'form' | 'credentials';

export const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  workspaceId,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('form');

  // Form fields
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  const [maxTokens, setMaxTokens] = useState('');

  // Credential display
  const [generatedKey, setGeneratedKey] = useState('');
  const [generatedSecret, setGeneratedSecret] = useState('');
  const [createdRecord, setCreatedRecord] = useState<ApiKey | null>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('O nome da chave é obrigatório');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: ApiKeyCreateRequest = {
        name: name.trim(),
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        notes: notes.trim() || null,
        max_tokens: maxTokens ? parseInt(maxTokens, 10) : null,
      };

      const created = await createApiKey(workspaceId, payload);
      const { api_secret, ...record } = created;

      setGeneratedKey(record.api_key);
      setGeneratedSecret(api_secret);
      setCreatedRecord(record);
      setStep('credentials');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao criar chave de API');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'key' | 'secret') => {
    await navigator.clipboard.writeText(text);
    if (type === 'key') {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } else {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const handleClose = () => {
    if (step === 'credentials' && createdRecord) {
      onSuccess(createdRecord);
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && (step === 'form' || acknowledged)) {
      handleClose();
    }
  };

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#2a2a2a] border border-[#333333] rounded-xl w-full max-w-lg mx-4 overflow-hidden shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#333333]">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 bg-[#F4CC04]/20 rounded-lg">
              <Key className="w-5 h-5 text-[#F4CC04]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">
                {step === 'form' ? 'Gerar nova chave de API' : 'Chave criada com sucesso'}
              </h2>
              <p className="text-xs text-[#888888]">
                {step === 'form'
                  ? `Workspace ${workspaceId}`
                  : 'Salve as credenciais agora — o segredo não será exibido novamente'}
              </p>
            </div>
          </div>
          {(step === 'form' || acknowledged) && (
            <button
              onClick={handleClose}
              className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Step 1 — Form */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[#cccccc] mb-1.5">
                Nome <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: sistema-erp-producao"
                maxLength={100}
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#444444] rounded-lg text-white text-sm placeholder-[#666666] focus:outline-none focus:border-[#F4CC04] transition-colors"
                autoFocus
              />
            </div>

            {/* Expiry */}
            <div>
              <label className="block text-sm font-medium text-[#cccccc] mb-1.5">
                Expira em <span className="text-[#888888] font-normal">(opcional)</span>
              </label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#444444] rounded-lg text-white text-sm focus:outline-none focus:border-[#F4CC04] transition-colors"
              />
            </div>

            {/* Token limit */}
            <div>
              <label className="block text-sm font-medium text-[#cccccc] mb-1.5">
                Limite de tokens <span className="text-[#888888] font-normal">(opcional — sem limite por padrão)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={maxTokens}
                onChange={(e) => setMaxTokens(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex: 500000"
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#444444] rounded-lg text-white text-sm placeholder-[#666666] focus:outline-none focus:border-[#F4CC04] transition-colors"
              />
              <p className="mt-1 text-xs text-[#666666]">
                Número máximo de tokens que esta chave pode consumir no total.
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[#cccccc] mb-1.5">
                Notas <span className="text-[#888888] font-normal">(opcional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Para que serve esta chave?"
                rows={2}
                className="w-full px-3 py-2.5 bg-[#1a1a1a] border border-[#444444] rounded-lg text-white text-sm placeholder-[#666666] focus:outline-none focus:border-[#F4CC04] transition-colors resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-[#888888] hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 bg-[#F4CC04] text-black rounded-lg text-sm font-medium hover:bg-[#e6be04] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Gerando...' : 'Gerar chave'}
              </button>
            </div>
          </form>
        )}

        {/* Step 2 — Credentials */}
        {step === 'credentials' && (
          <div className="px-6 py-5 space-y-4">
            {/* Warning banner */}
            <div className="flex items-start gap-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-orange-300">
                Copie o <strong>API Secret</strong> agora. Ele não poderá ser recuperado depois.
              </p>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-xs font-medium text-[#888888] mb-1.5 uppercase tracking-wider">
                API Key (identificador público)
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2.5 bg-[#1a1a1a] border border-[#333333] rounded-lg text-[#F4CC04] text-sm font-mono break-all">
                  {generatedKey}
                </code>
                <button
                  onClick={() => copyToClipboard(generatedKey, 'key')}
                  className="p-2.5 bg-[#1a1a1a] border border-[#333333] rounded-lg hover:border-[#F4CC04] text-[#888888] hover:text-[#F4CC04] transition-colors flex-shrink-0"
                  title="Copiar API Key"
                >
                  {copiedKey ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* API Secret */}
            <div>
              <label className="block text-xs font-medium text-[#888888] mb-1.5 uppercase tracking-wider">
                API Secret (mostrado apenas uma vez)
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2.5 bg-[#1a1a1a] border border-orange-500/30 rounded-lg text-orange-300 text-sm font-mono break-all">
                  {showSecret ? generatedSecret : '•'.repeat(Math.min(generatedSecret.length, 40))}
                </code>
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="p-2.5 bg-[#1a1a1a] border border-[#333333] rounded-lg hover:border-[#444444] text-[#888888] hover:text-white transition-colors flex-shrink-0"
                  title={showSecret ? 'Ocultar' : 'Mostrar'}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => copyToClipboard(generatedSecret, 'secret')}
                  className="p-2.5 bg-[#1a1a1a] border border-orange-500/30 rounded-lg hover:border-orange-400 text-orange-400 hover:text-orange-300 transition-colors flex-shrink-0"
                  title="Copiar API Secret"
                >
                  {copiedSecret ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Acknowledge checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-[#444444] bg-[#1a1a1a] accent-[#F4CC04] flex-shrink-0"
              />
              <span className="text-sm text-[#cccccc]">
                Confirmo que copiei o API Secret e entendo que ele não poderá ser recuperado.
              </span>
            </label>

            {/* Close button */}
            <div className="flex justify-end pt-1">
              <button
                onClick={handleClose}
                disabled={!acknowledged}
                className="px-5 py-2 bg-[#F4CC04] text-black rounded-lg text-sm font-medium hover:bg-[#e6be04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Concluir
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default CreateApiKeyModal;
